import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/config";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../../firebase/config";
import SendNotificationToCustomers from "../../components/SendNotificationToCustomers";
import SendNotificationButton from "../../components/SendNotificationButton";
import NotificationTest from "../../components/NotificationTest";

const AdminNotifications = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/admin/login");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();

        if (idTokenResult.claims.role === 'admin') {
          setAdmin({
            uid: user.uid,
            email: user.email,
            name: user.displayName,
            role: 'admin',
            permissions: idTokenResult.claims.permissions || []
          });
        } else {
          navigate("/admin/login");
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const notificationsQuery = query(
          collection(db, "notifications"), 
          orderBy("sentAt", "desc"), 
          limit(20)
        );
        const snapshot = await getDocs(notificationsQuery);
        
        const notificationsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          sentAt: doc.data().sentAt ? new Date(doc.data().sentAt) : new Date()
        }));
        
        setNotifications(notificationsList);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoadingNotifications(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case 'offer': return '🎉';
      case 'announcement': return '📢';
      case 'delivery': return '🚚';
      case 'reminder': return '⏰';
      case 'personal': return '👤';
      default: return '📱';
    }
  };

  // 🔔 Test Notification Function
  const sendTestNotification = async () => {
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../firebase/config');
      
      const sendTestNotification = httpsCallable(functions, 'sendTestNotification');
      
      const result = await sendTestNotification({
        title: 'Test Admin Notification',
        body: 'This is a test notification from the admin panel',
        targetRole: 'admin'
      });
      
      console.log('✅ Test notification result:', result.data);
      alert('✅ Test notification sent successfully!');
    } catch (error) {
      console.error('❌ Error sending test notification:', error);
      alert('❌ Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                ← Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
                <p className="text-gray-600">Notifications Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {admin?.name}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-green-700 mb-6">🔔 Notifications Management</h1>

        {/* 🔔 Test Notification Section */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">🧪 Test Notification System</h3>
          <p className="text-blue-600 mb-3">Test the notification system to ensure it's working properly.</p>
          <button
            onClick={sendTestNotification}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            🔔 Send Test Notification
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Send Notifications Section */}
          <div className="space-y-6">
            {/* Send to All Customers */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-green-100">
              <h2 className="text-xl font-bold text-green-700 mb-4">📤 Send to All Customers</h2>
              <SendNotificationToCustomers />
            </div>

            {/* Send to Specific User */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-green-100">
              <h2 className="text-xl font-bold text-green-700 mb-4">👤 Send to Specific User</h2>
              <SendNotificationButton />
            </div>

            {/* Debug Test Component */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-blue-100">
              <h2 className="text-xl font-bold text-blue-700 mb-4">🧪 Debug Test</h2>
              <NotificationTest />
            </div>
          </div>

          {/* Notification History */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">📋 Recent Notifications</h2>
            
            {loadingNotifications ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No notifications sent yet</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {notifications.map((notification) => (
                  <div key={notification.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-lg">{getNotificationTypeIcon(notification.type)}</span>
                          <h3 className="font-semibold text-gray-800">{notification.title}</h3>
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                            {notification.type}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-2">{notification.body}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>📅 {formatDate(notification.sentAt)}</span>
                          <span>📊 Sent: {notification.successCount || 0}</span>
                          {notification.failureCount > 0 && (
                            <span className="text-red-500">Failed: {notification.failureCount}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notification Statistics */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h2 className="text-lg font-bold text-blue-700 mb-3">📊 Notification Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div>
              <h3 className="font-semibold mb-2">Types of Notifications:</h3>
              <ul className="space-y-1">
                <li>• 🎉 Offers & Promotions</li>
                <li>• 📢 Announcements</li>
                <li>• 🚚 Delivery Updates</li>
                <li>• ⏰ Reminders</li>
                <li>• 👤 Personal Messages</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Best Practices:</h3>
              <ul className="space-y-1">
                <li>• Keep titles under 50 characters</li>
                <li>• Use clear, actionable messages</li>
                <li>• Send at appropriate times</li>
                <li>• Don't spam customers</li>
                <li>• Test with small groups first</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Features:</h3>
              <ul className="space-y-1">
                <li>• Send to all customers</li>
                <li>• Send to specific users</li>
                <li>• Pre-built templates</li>
                <li>• Delivery tracking</li>
                <li>• Analytics & history</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotifications; 